import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { StudentProgramsWithCourses } from '../../../../../api/types/students/programs/student-programs-with-courses';
import { CourseWithLecturesAssignmentsExamsEntity } from '../../../../../api/types/students/programs/student-programs-with-courses';

export interface StudyStatusPDFData {
  program: StudentProgramsWithCourses;
  studentName: string;
  studentId: string;
}

// docker run -it --name presenton -p 5000:80 -e LLM="openai" -e OPENAI_API_KEY="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" -e IMAGE_PROVIDER="dall-e-3" -e CAN_CHANGE_KEYS="false" -v "./app_data:/app_data" ghcr.io/presenton/presenton:latest

// Function to calculate final grade for a course based on assignments and exams
const calculateCourseFinalGrade = (
  course: CourseWithLecturesAssignmentsExamsEntity,
): number | null => {
  let totalScore = 0;
  let totalWeight = 0;

  // Calculate assignment grades
  if (course.assignments) {
    course.assignments.forEach((assignment) => {
      if (assignment.grade !== undefined && assignment.grade !== null) {
        const weight = assignment.assignmentWeight || 0;
        totalScore += assignment.grade * weight;
        totalWeight += weight;
      }
    });
  }

  // Calculate exam grades (take highest grade from each exam)
  if (course.exams) {
    course.exams.forEach((exam) => {
      let highestGrade = -1;

      exam.sessions.forEach((session) => {
        if (
          session.grade !== undefined &&
          session.grade !== null &&
          session.grade > highestGrade
        ) {
          highestGrade = session.grade;
        }
      });

      if (highestGrade >= 0) {
        const weight = exam.points || 0;
        totalScore += highestGrade * weight;
        totalWeight += weight;
      }
    });
  }

  return totalWeight > 0
    ? Math.round((totalScore / totalWeight) * 100) / 100
    : null;
};

// Function to create HTML content for the PDF
const createStudyStatusHTML = (data: StudyStatusPDFData): string => {
  const currentDate = new Date().toLocaleDateString('he-IL');

  // Calculate course data
  const courseRows = data.program.courses.map((course) => {
    const finalGrade = calculateCourseFinalGrade(course);

    return {
      name: course.courseName || 'שם קורס לא זמין',
      grade: finalGrade !== null ? finalGrade.toString() : 'ללא ציון',
      numericGrade: finalGrade,
    };
  });

  // Calculate average of completed courses
  const completedGrades = courseRows
    .map((course) => course.numericGrade)
    .filter((grade) => grade !== null) as number[];

  const averageGrade =
    completedGrades.length > 0
      ? Math.round(
          (completedGrades.reduce((sum, grade) => sum + grade, 0) /
            completedGrades.length) *
            100,
        ) / 100
      : null;

  return `
    <!DOCTYPE html>
    <html dir="rtl" lang="he">
    <head>
      <meta charset="UTF-8">
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Hebrew:wght@400;700&display=swap');
        
        body {
          font-family: 'Noto Sans Hebrew', Arial, sans-serif;
          direction: rtl;
          text-align: right;
          margin: 0;
          padding: 20px;
          background: white;
          color: black;
          font-size: 14px;
          line-height: 1.4;
        }
        
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #333;
          padding-bottom: 20px;
        }
        
        .company-name {
          font-size: 24px;
          font-weight: bold;
          color: #2c3e50;
          margin-bottom: 10px;
        }
        
        .report-title {
          font-size: 20px;
          font-weight: bold;
          color: #34495e;
          margin-bottom: 15px;
        }
        
        .date {
          font-size: 12px;
          color: #7f8c8d;
        }
        
        .student-info {
          margin: 30px 0;
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          border: 1px solid #dee2e6;
        }
        
        .info-row {
          margin-bottom: 8px;
          font-size: 16px;
        }
        
        .info-label {
          font-weight: bold;
          color: #2c3e50;
        }
        
        .program-grade {
          background: #e8f4fd;
          border: 1px solid #3498db;
          border-radius: 6px;
          padding: 15px;
          margin: 20px 0;
          text-align: center;
        }
        
        .program-grade-label {
          font-size: 18px;
          font-weight: bold;
          color: #2980b9;
        }
        
        .courses-section {
          margin-top: 30px;
        }
        
        .courses-title {
          font-size: 18px;
          font-weight: bold;
          color: #2c3e50;
          margin-bottom: 15px;
          text-align: right;
        }
        
        .courses-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .courses-table th {
          background: #34495e;
          color: white;
          padding: 12px 8px;
          text-align: center;
          font-weight: bold;
          border: 1px solid #2c3e50;
        }
        
        .courses-table td {
          padding: 10px 8px;
          text-align: center;
          border: 1px solid #bdc3c7;
        }
        
        .courses-table tr:nth-child(even) {
          background: #f8f9fa;
        }
        
        .courses-table tr:hover {
          background: #e8f4fd;
        }
        
        .course-name {
          text-align: right !important;
          font-weight: 500;
          max-width: 200px;
        }
        
        .grade-cell {
          font-weight: bold;
          color: #27ae60;
        }
        
        .no-grade {
          color: #e74c3c;
          font-style: italic;
        }
        
        .footer {
          margin-top: 40px;
          text-align: center;
          font-size: 12px;
          color: #7f8c8d;
          border-top: 1px solid #dee2e6;
          padding-top: 20px;
        }
        
        @media print {
          body { 
            font-size: 12px; 
            padding: 15px;
          }
          .header { 
            margin-bottom: 20px; 
          }
          .courses-table { 
            font-size: 11px; 
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company-name">המרכז להכשרה מקצועית</div>
        <div class="report-title">מצב לימודים</div>
        <div class="date">תאריך: ${currentDate}</div>
      </div>
      
      <div class="student-info">
        <div class="info-row">
          <span class="info-label">שם הסטודנט:</span> ${data.studentName}
        </div>
        <div class="info-row">
          <span class="info-label">מספר זהות:</span> ${data.studentId}
        </div>
        <div class="info-row">
          <span class="info-label">תוכנית לימודים:</span> ${data.program.programName}
        </div>
      </div>
      
      <div class="program-grade">
        <span class="program-grade-label">
          ציון כללי לתוכנית: ${data.program.finalGrade !== null && data.program.finalGrade !== undefined ? data.program.finalGrade : 'טרם הושלמו הקורסים'}
        </span>
      </div>
      
      ${
        averageGrade !== null
          ? `
        <div class="program-grade">
          <span class="program-grade-label">ממוצע קורסים שהושלמו: ${averageGrade}</span>
        </div>
      `
          : ''
      }
      
      <div class="courses-section">
        <div class="courses-title">רשימת קורסים וציונים:</div>
        
        <table class="courses-table">
          <thead>
            <tr>
              <th>שם הקורס</th>
              <th>ציון</th>
            </tr>
          </thead>
          <tbody>
            ${courseRows
              .map(
                (course) => `
              <tr>
                <td class="course-name">${course.name}</td>
                <td class="grade-cell ${course.grade === 'ללא ציון' ? 'no-grade' : ''}">${course.grade}</td>
              </tr>
            `,
              )
              .join('')}
          </tbody>
        </table>
      </div>
      
      <div class="footer">
        <div>נוצר ב: ${currentDate}</div>
        <div>מערכת ניהול האקדמיה</div>
      </div>
    </body>
    </html>
  `;
};

export const generateStudyStatusPDF = async (
  data: StudyStatusPDFData,
): Promise<void> => {
  try {
    // Create HTML content
    const htmlContent = createStudyStatusHTML(data);

    // Create a temporary container element
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlContent;
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.style.width = '210mm'; // A4 width
    tempContainer.style.background = 'white';

    // Append to body
    document.body.appendChild(tempContainer);

    // Wait for fonts to load
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      // Convert HTML to canvas
      const canvas = await html2canvas(tempContainer, {
        allowTaint: true,
        useCORS: true,
        scale: 2, // Higher scale for better quality
        width: 794, // A4 width in pixels at 96 DPI
        height: 1123, // A4 height in pixels at 96 DPI
        backgroundColor: '#ffffff',
        logging: false,
      });

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      // Calculate dimensions
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Add image to PDF
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

      // If content is longer than one page, add additional pages
      if (imgHeight > 297) {
        // A4 height in mm
        let position = 297;
        while (position < imgHeight) {
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, -position, imgWidth, imgHeight);
          position += 297;
        }
      }

      // Generate filename
      const currentDate = new Date().toLocaleDateString('he-IL');
      const fileName = `מצב_לימודים_${data.program.programName.replace(/\s+/g, '_')}_${currentDate.replace(/\//g, '-')}.pdf`;

      // Download the PDF
      pdf.save(fileName);
    } finally {
      // Clean up: remove temporary container
      document.body.removeChild(tempContainer);
    }
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('שגיאה ביצירת קובץ PDF');
  }
};
